import React from 'react';
import { format } from 'date-fns';
import { Bot, User, CheckCheck, Clock, AlertCircle, Paperclip } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Message, UIResource } from '@/types/chat';
import { MarkdownMessage } from './MarkdownMessage';
import { MessageActions } from './MessageActions';
import { UIResourceRenderer } from './UIResourceRenderer';

interface ChatMessageProps {
  message: Message;
  onCopy?: () => void;
  onRegenerate?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message,
  onCopy,
  onRegenerate,
  onEdit,
  onDelete,
}) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  
  // Check if content is a UIResource
  const isResource = typeof message.content === 'object' && 
                     message.content !== null &&
                     'type' in message.content && 
                     message.content.type === 'resource';
  
  // Debug logging
  if (typeof message.content === 'object' && message.content !== null) {
    console.log('🔍 ChatMessage content type:', typeof message.content);
    console.log('🔍 ChatMessage has type field:', 'type' in message.content);
    console.log('🔍 ChatMessage type value:', (message.content as any).type);
    console.log('🔍 ChatMessage isResource:', isResource);
    console.log('🔍 ChatMessage content:', message.content);
  }

  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <div className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
          {typeof message.content === 'string' ? message.content : 'System message'}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group flex gap-3 mb-4 animate-in fade-in slide-in-from-bottom-2',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        )}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      {/* Message Content */}
      <div className={cn('flex flex-col gap-1 min-w-0', isUser ? 'items-end' : 'items-start', isResource ? 'flex-1' : '')}>
        <div
          className={cn(
            'rounded-2xl',
            isResource ? 'w-full' : 'max-w-[85%] px-4 py-2',
            isUser
              ? 'bg-primary text-primary-foreground rounded-br-sm'
              : isResource
                ? ''
                : 'bg-muted text-foreground rounded-bl-sm'
          )}
        >
          {isUser ? (
            <div className="text-sm whitespace-pre-wrap" style={{ overflowWrap: 'break-word', wordBreak: 'keep-all', minWidth: 'fit-content' }}>
              {/* File attachment indicators */}
              {message.metadata?.files && Array.isArray(message.metadata.files) && message.metadata.files.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {message.metadata.files.map((file: { fileName: string; mimeType?: string }, i: number) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-foreground/20 text-[11px]"
                    >
                      <Paperclip className="h-3 w-3" />
                      <span className="truncate max-w-[150px]">{file.fileName}</span>
                    </span>
                  ))}
                </div>
              )}
              {typeof message.content === 'string' ? message.content : 'Invalid message'}
            </div>
          ) : isResource ? (
            <UIResourceRenderer resource={message.content as UIResource} />
          ) : (
            <MarkdownMessage content={message.content as string} className="text-sm" />
          )}
        </div>

        {/* Metadata & Actions */}
        <div className="flex items-center gap-2 px-2">
          <span className="text-xs text-muted-foreground">
            {format(new Date(message.timestamp), 'HH:mm')}
          </span>

          {isUser && message.status && (
            <span className="text-xs">
              {message.status === 'sending' && (
                <Clock className="w-3 h-3 text-muted-foreground animate-pulse" />
              )}
              {message.status === 'sent' && (
                <CheckCheck className="w-3 h-3 text-muted-foreground" />
              )}
              {message.status === 'error' && (
                <AlertCircle className="w-3 h-3 text-destructive" />
              )}
            </span>
          )}

          {/* Message Actions */}
          <MessageActions
            content={typeof message.content === 'string' ? message.content : JSON.stringify(message.content, null, 2)}
            messageId={message.id}
            role={message.role}
            onCopy={onCopy}
            onRegenerate={onRegenerate}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      </div>
    </div>
  );
};
